import { useEffect, useState, useCallback } from "react";
import { RotateCw } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, formatApiErrorDetail } from "../lib/api";
import { TypeBadge, PriorityBadge } from "../components/badges";

const TYPES = ["Tous", "STATUS", "TASK", "RESULT", "ALERT", "METRICS", "EVENT"];

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [queue, setQueue] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    const q = filter === "Tous" ? "" : `?type=${filter}`;
    api.get(`/messages${q}`).then(({ data }) => setMessages(data)).catch(() => {});
    api.get("/queue").then(({ data }) => setQueue(data)).catch(() => {});
  }, [filter]);

  const retry = async (id) => {
    try {
      const { data } = await api.post(`/queue/${id}/retry`);
      data.queue_status === "delivered"
        ? toast.success("Message relivré avec succès")
        : toast.error(`Relance échouée : ${data.error || data.queue_status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div data-testid="messages-page" className="relative space-y-6">
      <div aria-hidden="true" className="pointer-events-none absolute -top-28 left-1/3 h-64 w-[44rem] -translate-x-1/2 rounded-full bg-[#00E5FF]/[0.05] blur-3xl" />
      <div className="relative">
        <h1 className="font-heading text-4xl font-bold tracking-tight text-white">Messages</h1>
        <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">Journal central des communications — protocole universel</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TYPES.map((t) => (
          <button key={t} data-testid={`filter-${t.toLowerCase()}`} onClick={() => setFilter(t)}
            className={`border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-150 ${filter === t ? "border-primary/60 bg-primary/10 text-primary" : "border-border text-neutral-500 hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>

      {queue.length > 0 && (
        <div className="border border-amber-500/30 bg-card shadow-[0_0_20px_rgba(245,158,11,0.06)]">
          <div className="border-b border-amber-500/30 px-5 py-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-300">
            File sortante — {queue.length} message(s) non livré(s)
          </div>
          <div data-testid="queue-panel" className="divide-y divide-border">
            {queue.map((it) => (
              <div key={it.id} className="flex items-center justify-between px-5 py-2.5 font-mono text-[11px]">
                <div className="flex items-center gap-3">
                  <TypeBadge type={it.type} />
                  <span className="text-white">→ {it.destination}</span>
                  <span className={it.status === "failed" ? "text-red-400 uppercase" : "text-amber-300 uppercase"}>{it.status}</span>
                  <span className="text-neutral-600">tentatives {it.attempts}/{it.max_attempts}</span>
                  {it.last_error && <span className="max-w-[240px] truncate text-neutral-500">{it.last_error}</span>}
                  {it.next_attempt_at && <span className="text-neutral-600">prochain essai {fmtDate(it.next_attempt_at)}</span>}
                </div>
                <button data-testid={`queue-retry-${it.id}`} onClick={() => retry(it.id)}
                  className="flex items-center gap-1.5 border border-border px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-primary transition-colors duration-150 hover:border-primary/50">
                  <RotateCw size={11} /> Relancer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto border border-white/10 bg-card shadow-[0_0_20px_rgba(0,229,255,0.04)]">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-border font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <th className="px-4 py-3">Horodatage</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Source → Destination</th>
              <th className="px-4 py-3">Priorité</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Payload</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {messages.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center font-mono text-xs text-neutral-600">Aucun message</td></tr>
            )}
            {messages.map((m) => (
              <tr key={m.id} data-testid="message-row" onClick={() => setExpanded(expanded === m.id ? null : m.id)}
                className="cursor-pointer transition-colors duration-100 hover:bg-muted/50">
                <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[11px] text-neutral-400">{fmtDate(m.created_at)}</td>
                <td className="px-4 py-2.5"><TypeBadge type={m.type} /></td>
                <td className="px-4 py-2.5 font-mono text-xs text-white">{m.source} <span className="text-primary">→</span> {m.destination}</td>
                <td className="px-4 py-2.5"><PriorityBadge priority={m.priority} /></td>
                <td className="px-4 py-2.5 font-mono text-[10px] uppercase text-neutral-500">{m.status}</td>
                <td className="max-w-[300px] px-4 py-2.5 font-mono text-[10px] text-neutral-500">
                  {expanded === m.id
                    ? <div>
                        <div className="mb-1 text-neutral-400">v{m.schema_version || "1.0"}{m.correlation_id ? ` · corr: ${m.correlation_id}` : ""}{m.expires_at ? ` · expire: ${fmtDate(m.expires_at)}` : ""}</div>
                        <pre className="whitespace-pre-wrap text-primary/80">{JSON.stringify(m.payload, null, 2)}</pre>
                      </div>
                    : <span className="block truncate">{JSON.stringify(m.payload)}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
